import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLanguage } from '@/contexts/LanguageContext'
import {
  LANGUAGE_NAMES,
  SupportedLanguage,
  SUPPORTED_LANGUAGES,
  LANGUAGE,
} from '@/i18n'
import { cn } from '@/lib/utils.ts'

interface LanguageSwitcherProps {
  children?: ReactNode
}

export function LanguageSwitcher({ children }: LanguageSwitcherProps) {
  const { t } = useTranslation()
  const { language, changeLanguage } = useLanguage()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {children || (
          <Button variant='outline' size='icon'>
            <Globe className='h-5 w-5' />
            <span className='sr-only'>Switch language</span>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='min-w-[200px] p-0'>
        <div className='px-4 py-3'>
          <h3 className='text-sm font-semibold text-gray-700'>
            {t('common.chooseLanguage')}
          </h3>
        </div>
        {SUPPORTED_LANGUAGES.map(lang => (
          <DropdownMenuItem
            key={lang}
            onClick={() => changeLanguage(lang as SupportedLanguage)}
            className={cn(
              'flex items-center gap-3 rounded-lg cursor-pointer mb-2',
              'hover:bg-[#F7F8FA] transition-colors',
              language === lang && 'bg-[#F7F8FA]'
            )}
          >
            <div className='w-8 h-6 flex items-center justify-center border border-gray-300 rounded-2xl text-xs'>
              {LANGUAGE[lang as SupportedLanguage]}
            </div>
            <span className={language === lang ? 'font-bold' : ''}>
              {LANGUAGE_NAMES[lang as SupportedLanguage]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
